import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { getRegion, regions } from "@/data/regions";
import { services } from "@/data/services";
import { images } from "@/data/images";
import { regionFaq } from "@/data/faq";
import { Section, SectionHeading } from "@/components/site/Section";
import { QuoteForm } from "@/components/site/QuoteForm";
import { ReviewsBlock } from "@/components/site/ReviewsBlock";
import { FaqBlock } from "@/components/site/FaqBlock";
import { CtaBand } from "@/components/site/CtaBand";
import { JsonLd } from "@/components/site/JsonLd";
import { ReviewBar } from "@/components/site/ReviewBar";
import { breadcrumbSchema, faqSchema, regionSchema, meta } from "@/lib/schema";
import { regionCostBlock, regionMetaDescription, regionSeoBlocks, regionTitle } from "@/lib/regionSeo";
import { regionKeywords } from "@/lib/localKeywords";

export const Route = createFileRoute("/werkgebied/$slug")({
  loader: ({ params }) => {
    const region = getRegion(params.slug);
    if (!region) throw notFound();
    return { region };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Plaats niet gevonden" }, { name: "robots", content: "noindex" }] };
    }
    const { region } = loaderData;
    return meta({
      title: regionTitle(region),
      description: regionMetaDescription(region),
      path: `/werkgebied/${region.slug}`,
      image: "team",
      keywords: regionKeywords(region),
    });
  },
  component: RegionDetail,
});

function RegionDetail() {
  const { region } = Route.useLoaderData();
  const nearby = region.nearby
    .map((slug) => regions.find((r) => r.slug === slug))
    .filter((r): r is (typeof regions)[number] => Boolean(r));
  const costBlock = regionCostBlock(region);
  const localFaq = [...regionFaq(region), ...costBlock.faq];
  const seoBlocks = regionSeoBlocks(region);
  const sameGroup = regions.filter((r) => r.group === region.group && r.slug !== region.slug);

  return (
    <>
      <JsonLd
        data={regionSchema({
          city: region.city,
          slug: region.slug,
          neighbourhoods: region.neighbourhoods,
          group: region.group,
        })}
      />
      <JsonLd data={faqSchema(localFaq)} />
      <JsonLd
        data={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Werkgebied", path: "/werkgebied" },
          { name: `Verhuisbedrijf ${region.city}`, path: `/werkgebied/${region.slug}` },
        ])}
      />

      <section className="bg-secondary text-secondary-foreground">
        <div className="container-page grid gap-10 py-14 lg:grid-cols-2 lg:items-center">
          <div>
            <nav className="text-xs font-semibold uppercase tracking-[0.14em] text-brand">
              <Link to="/werkgebied">Werkgebied</Link> <span aria-hidden>/</span> {region.group}{" "}
              <span aria-hidden>/</span> {region.city}
            </nav>
            <h1 className="mt-4 font-display text-3xl font-extrabold sm:text-4xl">
              Verhuisbedrijf {region.city}
            </h1>
            <p className="mt-4 max-w-xl opacity-90">{region.intro}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/offerte"
                className="rounded-lg bg-brand px-5 py-3 text-sm font-bold text-brand-foreground hover:opacity-90"
              >
                Gratis offerte voor {region.city}
              </Link>
              <a
                href="#diensten"
                className="rounded-lg border border-current px-5 py-3 text-sm font-bold hover:text-brand"
              >
                Bekijk diensten in {region.city}
              </a>
            </div>
          </div>
          <img
            src={images.team}
            alt={`Verhuiswagens van Vakkundig Verhuizingen & Transport klaar voor een verhuizing in ${region.city}`}
            className="rounded-2xl object-cover"
          />
        </div>
      </section>

      <ReviewBar />

      <nav aria-label={`Onderwerpen over verhuizen in ${region.city}`} className="border-b border-border bg-muted">
        <div className="container-page flex flex-wrap gap-2 py-4 text-sm font-semibold">
          {[
            { href: "#verhuizen", label: `Verhuizen ${region.city}` },
            { href: "#kosten", label: `Wat kost verhuizen in ${region.city}?` },
            { href: "#verhuislift", label: `Verhuislift huren ${region.city}` },
            { href: "#zakelijk", label: `Kantoorverhuizing ${region.city}` },
            { href: "#wijken", label: `Wijken in ${region.city}` },
            { href: "#diensten", label: `Diensten in ${region.city}` },
            { href: "#vragen", label: `Vragen over verhuizen in ${region.city}` },
          ].map((a) => (
            <a
              key={a.href}
              href={a.href}
              className="rounded-full border border-border bg-background px-3 py-1.5 text-secondary transition hover:border-brand hover:text-brand"
            >
              {a.label}
            </a>
          ))}
        </div>
      </nav>

      <Section>
        <div className="grid gap-12 lg:grid-cols-[1.4fr_1fr]">
          <div>
            <h2 id="verhuizen" className="scroll-mt-28 font-display text-2xl font-bold text-secondary">
              Verhuizen in {region.city}: wat wij voor je regelen
            </h2>
            <p className="mt-3 leading-relaxed text-muted-foreground">{region.local}</p>

            {seoBlocks.map((block) => (
              <div key={block.h2}>
                <h2
                  id={
                    block.h2.includes("Verhuislift")
                      ? "verhuislift"
                      : block.h2.includes("Zakelijke")
                        ? "zakelijk"
                        : undefined
                  }
                  className="mt-10 scroll-mt-28 font-display text-2xl font-bold text-secondary"
                >
                  {block.h2}
                </h2>
                <p className="mt-3 leading-relaxed text-muted-foreground">{block.body}</p>
              </div>
            ))}


            <h2 id="kosten" className="mt-10 scroll-mt-28 font-display text-2xl font-bold text-secondary">
              {costBlock.h2}
            </h2>
            <p className="mt-3 leading-relaxed text-muted-foreground">{costBlock.body}</p>
            <ul className="mt-4 space-y-2 text-muted-foreground">
              {costBlock.bullets.map((b) => (
                <li key={b} className="flex gap-2">
                  <span aria-hidden className="mt-1 text-brand">
                    •
                  </span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <p className="mt-4 flex flex-wrap gap-3">
              <Link
                to="/offerte"
                className="rounded-lg bg-brand px-5 py-3 text-sm font-bold text-brand-foreground hover:opacity-90"
              >
                Vrijblijvende offerte voor {region.city}
              </Link>
              <Link
                to="/verhuiskosten"
                className="rounded-lg border border-border px-5 py-3 text-sm font-bold text-secondary hover:border-brand"
              >
                Meer over verhuiskosten
              </Link>
            </p>

            <h2 id="wijken" className="mt-10 scroll-mt-28 font-display text-2xl font-bold text-secondary">
              Wijken en buurten waar we verhuizen
            </h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {region.neighbourhoods.map((n) => (
                <span
                  key={n}
                  className="rounded-full border border-border bg-muted px-3 py-1.5 text-sm font-semibold text-secondary"
                >
                  {n}
                </span>
              ))}
            </div>

            <h2 id="diensten" className="mt-10 scroll-mt-28 font-display text-2xl font-bold text-secondary">
              Onze diensten in {region.city}
            </h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              {services.map((s) => (
                <Link
                  key={s.slug}
                  to="/diensten/$slug"
                  params={{ slug: s.slug }}
                  className="rounded-lg border border-border bg-background p-4 transition hover:border-brand"
                >
                  <span className="font-semibold text-secondary">
                    {s.title} in {region.city}
                  </span>
                  <span className="mt-1 block text-sm text-muted-foreground">{s.short}</span>
                </Link>
              ))}
            </div>

            <div className="mt-8 rounded-2xl border border-border bg-muted p-6">
              <h2 className="font-display text-xl font-bold text-secondary">
                Direct verder met je verhuizing in {region.city}
              </h2>
              <ul className="mt-4 grid gap-2 text-sm sm:grid-cols-2">
                {[
                  { slug: "particuliere-verhuizing", label: `Particuliere verhuizing ${region.city}` },
                  { slug: "verhuislift", label: `Verhuislift huren in ${region.city}` },
                  { slug: "zakelijke-verhuizing", label: `Bedrijfsverhuizing ${region.city}` },
                  { slug: "seniorenverhuizing", label: `Seniorenverhuizing ${region.city}` },
                  { slug: "inpakservice", label: `Inpakservice in ${region.city}` },
                  { slug: "opslag", label: `Inboedelopslag ${region.city}` },
                ].map((l) => (
                  <li key={l.slug}>
                    <Link
                      to="/diensten/$slug"
                      params={{ slug: l.slug }}
                      className="font-semibold text-secondary hover:text-brand"
                    >
                      {l.label} →
                    </Link>
                  </li>
                ))}
              </ul>
              <p className="mt-5 text-sm text-muted-foreground">
                Binnenkort verhuizen in {region.city}? Vraag binnen 2 minuten een vrijblijvende offerte aan.
              </p>
              <Link
                to="/offerte"
                className="mt-3 inline-block rounded-lg bg-brand px-5 py-3 text-sm font-bold text-brand-foreground hover:opacity-90"
              >
                Offerte aanvragen voor {region.city}
              </Link>
            </div>

            <h2 id="vragen" className="mt-10 scroll-mt-28 font-display text-2xl font-bold text-secondary">
              Veelgestelde vragen over verhuizen in {region.city}
            </h2>
            <div className="mt-4">
              <FaqBlock items={localFaq} />
            </div>
          </div>

          <aside className="lg:sticky lg:top-28 lg:self-start">
            <div className="rounded-2xl border border-border bg-background p-6 shadow-[var(--shadow-card)]">
              <h2 className="font-display text-xl font-extrabold text-secondary">
                Offerte voor {region.city}
              </h2>
              <div className="mt-4">
                <QuoteForm compact />
              </div>
            </div>
            <div className="mt-6 rounded-2xl border border-border bg-background p-6">
              <h2 className="font-display text-lg font-bold text-secondary">
                Handig om te weten voor je verhuizing in {region.city}
              </h2>
              <ul className="mt-3 space-y-2 text-sm">
                <li>
                  <Link to="/verhuiskosten" className="font-semibold text-secondary hover:text-brand">
                    Wat kost een verhuizing?
                  </Link>
                </li>
                <li>
                  <Link to="/piano-verhuizen" className="font-semibold text-secondary hover:text-brand">
                    Piano of vleugel verhuizen
                  </Link>
                </li>
                <li>
                  <Link to="/studentenverhuizing" className="font-semibold text-secondary hover:text-brand">
                    Studentenverhuizing
                  </Link>
                </li>
                <li>
                  <Link to="/internationaal-verhuizen" className="font-semibold text-secondary hover:text-brand">
                    Internationaal verhuizen
                  </Link>
                </li>
                <li>
                  <Link
                    to="/diensten/$slug"
                    params={{ slug: "zakelijke-verhuizing" }}
                    className="font-semibold text-secondary hover:text-brand"
                  >
                    Bedrijfsverhuizing {region.city}
                  </Link>
                </li>
                <li>
                  <Link to="/kantoorverhuizing" className="font-semibold text-secondary hover:text-brand">
                    Kantoorverhuizing: zo werkt het
                  </Link>
                </li>
                <li>
                  <Link to="/spoedverhuizing" className="font-semibold text-secondary hover:text-brand">
                    Spoedverhuizing op korte termijn
                  </Link>
                </li>
                <li>
                  <Link to="/woningontruiming-den-haag" className="font-semibold text-secondary hover:text-brand">
                    Woningontruiming
                  </Link>
                </li>
              </ul>
            </div>
            <div className="mt-6 rounded-2xl border border-border bg-muted p-6">

              <h2 className="font-display text-lg font-bold text-secondary">
                Ook actief in de buurt van {region.city}
              </h2>
              <ul className="mt-3 space-y-2 text-sm">
                {nearby.map((r) => (
                  <li key={r.slug}>
                    <Link
                      to="/werkgebied/$slug"
                      params={{ slug: r.slug }}
                      className="font-semibold text-secondary hover:text-brand"
                    >
                      Verhuisbedrijf {r.city}
                    </Link>
                  </li>
                ))}
              </ul>
              {sameGroup.length ? (
                <>
                  <h3 className="mt-6 font-display text-base font-bold text-secondary">
                    Meer plaatsen in {region.group}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {sameGroup.map((r, i) => (
                      <span key={r.slug}>
                        {i > 0 ? ", " : ""}
                        <Link
                          to="/werkgebied/$slug"
                          params={{ slug: r.slug }}
                          className="font-semibold text-secondary hover:text-brand"
                        >
                          {r.city}
                        </Link>
                      </span>
                    ))}
                  </p>
                </>
              ) : null}
              <Link to="/werkgebied" className="mt-5 inline-block text-sm font-bold text-brand hover:underline">
                Volledig werkgebied bekijken →
              </Link>
            </div>
          </aside>
        </div>
      </Section>

      <Section tone="muted">
        <SectionHeading
          eyebrow="Google-reviews"
          title={`Wat klanten zeggen over onze verhuizingen in en rond ${region.city}`}
        />
        <div className="mt-8">
          <ReviewsBlock limit={3} source="Google" />
        </div>
        <div className="mt-8 flex flex-col gap-4 rounded-2xl border border-border bg-background p-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="font-semibold text-secondary">
            Ook zorgeloos verhuizen in {region.city}? Sluit je aan bij honderden tevreden klanten.
          </p>
          <Link
            to="/offerte"
            className="shrink-0 rounded-lg bg-brand px-5 py-3 text-center text-sm font-bold text-brand-foreground hover:opacity-90"
          >
            Offerte aanvragen voor {region.city} →
          </Link>
        </div>
      </Section>

      <CtaBand />
    </>
  );
}
